import { json, type ActionFunctionArgs } from "@remix-run/node"
import { Private } from "~/utils/remix"
import { withZod } from "@remix-validated-form/with-zod"
import { z } from "zod"
import { validationError } from "remix-validated-form"
import {
  CreateCustomerPaymentProfile,
  CreateCustomerProfile,
  DeleteCustomerPaymentProfile,
} from "~/utils/authorize/cards.server"
import { UserPayment, Users } from "@prisma/client"
import { CardArgs } from "~/utils/authorize/cards.server"
import * as utils from "~/utils/authorize/utils"
import creditCardType from "credit-card-type"

const cardSchema = z.object({
  UserPaymentID: z.coerce.number().optional(),
  Address: z
    .string()
    .min(1, { message: "address is required" })
    .max(60, { message: "Address cannot be longer than 60 characters" }),
  Country: z.string().min(1, { message: "country is required" }),
  City: z.string().min(1, { message: "city is required" }),
  State: z.string().optional(), // TODO optional only if not US?
  Zip: z.string().min(1, { message: "zip code is required" }),
  CardHolder: z.string().min(1, { message: "card holder is required" }),
  CardNumber: z
    .string()
    .min(16, { message: "card number must be 16 digits" })
    .max(16, { message: "card number must be 16 digits" }),
  CardMonth: z.string().min(1, { message: "month required" }),
  CardYear: z.string().min(1, { message: "year required" }),
  CardCode: z.string().min(3, { message: "CVV must be 3 digits" }).max(3, { message: "CVV must be 3 digits" }),
})
export const cardValidator = withZod(cardSchema)

const deleteCardSchema = z.object({
  UserPaymentID: z.coerce.number(),
})
export const deleteCardValidator = withZod(deleteCardSchema)

function getCardArgs(user: Users, data: z.infer<typeof cardSchema>): CardArgs {
  return {
    Email: user.Email!,
    ExpirationDate: `${data.CardYear}-${data.CardMonth}`,
    State: data.State!,
    ...data,
  }
}

export const action = Private(async (args: ActionFunctionArgs) => {
  switch (args.request.method) {
    case "POST":
      return createCard(args)
    case "PUT":
    //return updateCard(args);
    case "DELETE":
      return deleteCard(args)
    default:
      return json({ error: "Method not allowed" }, { status: 405 })
  }
})

const createCard = async ({ request, context }: ActionFunctionArgs) => {
  const db = context.db
  const user = context.user!

  const result = await cardValidator.validate(await request.formData())
  if (result.error) {
    return validationError(result.error)
  }

  const cardArgs = getCardArgs(user, result.data)

  let customerProfileID = user.CustomerProfileId
  let paymentProfileID: string | null = null

  if (customerProfileID) {
    // Create card in Auth.net
    const createCard = await CreateCustomerPaymentProfile(context, customerProfileID, cardArgs)
    if (!createCard.success) {
      return json({ error: friendlyErrorMessage(createCard) }, { status: 400 })
    }

    paymentProfileID = createCard.response.getCustomerPaymentProfileId()
  } else {
    // Create customer and card in Auth.net
    const createCard = await CreateCustomerProfile(context, cardArgs)
    if (!createCard.success) {
      return json({ error: friendlyErrorMessage(createCard) }, { status: 400 })
    }

    customerProfileID = createCard.response.getCustomerProfileId()
    paymentProfileID = createCard.response.customerPaymentProfileIdList.numericString[0].toString()

    await db.users.update({
      where: { UserID: user.UserID },
      data: { CustomerProfileId: customerProfileID },
    })
  }

  const CardType = creditCardType(cardArgs.CardNumber)[0].niceType
  const CardNumber = `XXXX${cardArgs.CardNumber.slice(-5)}`

  const card = await db.userPayment.create({
    data: {
      UserID: user.UserID,
      CognitoUserID: user.CognitoUserID,
      CustomerProfileId: customerProfileID,
      PaymentProfileId: paymentProfileID,
      CardType,
      CardExpDate: cardArgs.ExpirationDate,
      CardHolder: cardArgs.CardHolder,
      CardNumber,
      CardStatus: 1,
      Email: user.Email,
      Address: cardArgs.Address,
      City: cardArgs.City,
      Country: cardArgs.Country,
      State: cardArgs.State,
      Zip: cardArgs.Zip,
    },
  })

  return json({ card: cardResponse(card) })
}

const deleteCard = async ({ request, context }: ActionFunctionArgs) => {
  const db = context.db
  const user = context.user!

  const form = await deleteCardValidator.validate(await request.formData())
  if (form.error) {
    return validationError(form.error)
  }

  const existingCard = await db.userPayment.findFirst({
    where: { UserID: user.UserID, UserPaymentID: form.data.UserPaymentID },
  })
  if (!existingCard) {
    return json({ error: "Card does not exist." }, { status: 400 })
  }

  // Delete card
  let result = await DeleteCustomerPaymentProfile(
    context,
    existingCard.CustomerProfileId || user.CustomerProfileId!,
    existingCard.PaymentProfileId!
  )
  if (!result.success) {
    return json({ error: result }, { status: 500 })
  }

  // Delete card in DB
  await db.userPayment.update({
    where: { UserID: user.UserID, UserPaymentID: existingCard.UserPaymentID },
    data: { Deleted: 1 },
  })

  return json({})
}

export const cardResponse = (card: UserPayment) => {
  card.CardHolder = card.CardHolder ? card.CardHolder : `${card.FirstName} ${card.LastName}`
  return card
}

export const friendlyErrorMessage = (resp: utils.Resp) => {
  switch (resp.errorCode) {
    case "2":
      return {
        alert: "Your card has been declined",
        description:
          "Please contact your bank or credit card company to allow the transaction before attempting again.",
        class: "error",
        response: resp,
      }
    case "4":
      return {
        alert: "Your card has been declined",
        description:
          "Please contact your bank or credit card company to allow the transaction before attempting again.",
        class: "error",
        response: resp,
      }
    case "27":
      return {
        alert: "Incorrect address",
        description:
          "The billing address you provided does not match your credit card. Please update your billing address and try again.",
        class: "error",
        response: resp,
      }
    case "37":
      return {
        alert: "Invalid credit card number",
        description: "Your credit card number is invalid. Please update your credit card info and try again.",
        class: "error",
        response: resp,
      }
    case "8":
      return {
        alert: "Credit card is expired",
        description: "Your credit card has expired. Please update your credit card info and try again.",
        class: "error",
        response: resp,
      }
    default:
      return {
        alert: "There was an error processing your card",
        description: resp.errorMessage,
        class: "error",
        response: resp,
      }
  }
}
